'use client'

import React, { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { FormInput } from '@/components/ui/forms/FormInput'
import { useAddressAutocompleteEnabled } from '@/hooks/useAddressAutocompleteEnabled'
import { getAddressSuggestions } from '@/services/shared/address'
import type { AddressSuggestionSchemaType } from '@/types/portal-api.generated'
import { BaseFormFieldProps } from '@/types/form'
import { isAbortError } from '@/utilities/abort'
import { errorAnalyticsPayload } from '@/utilities/analytics/errorAnalytics'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { getHttpResponseStatus } from '@/utilities/axios/errorUtils'
import { getPortalType, isClientPortal } from '@/utilities/stytch/config'
import { cn } from '@/utilities/ui'
import { useDebounce } from '@/utilities/useDebounce'

const DEBOUNCE_MS = 300
const MIN_QUERY_LENGTH = 3

/** Matches client portal Dropdown / form field focus styling (not ui/Input's dark ring). */
const CLIENT_PORTAL_INPUT_FOCUS_CLASSES = cn(
  'focus:outline-none focus:ring-0 focus:ring-cp-bright-blue focus:ring-offset-0',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-bright-blue',
  'focus-visible:ring-1 focus-visible:ring-cp-bright-blue focus-visible:ring-offset-0',
  'focus:border-cp-bright-blue focus-visible:border-cp-bright-blue',
)

export const ADDRESS_AUTOCOMPLETE_SUGGESTIONS_FAIL = 'address_autocomplete:suggestions_fail'

export type AddressSuggestionFetcher = typeof getAddressSuggestions

export type AddressAutocompleteSurface =
  | 'kit_registration'
  | 'address_correction'
  | 'user_profile'
  | 'order_patient'
  | 'order_shipping'
  | 'order_billing'
  | 'order_secondary_insurance'
  | 'pcp'

function queryLengthBucket(length: number): '3-5' | '6-10' | '11-20' | '21+' {
  if (length <= 5) return '3-5'
  if (length <= 10) return '6-10'
  if (length <= 20) return '11-20'
  return '21+'
}

export type AddressAutocompleteValue = {
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  zipCode: string
}

type AddressAutocompleteInputProps = BaseFormFieldProps & {
  id: string
  testId: string
  surface: AddressAutocompleteSurface
  onAddressSelect: (address: AddressAutocompleteValue) => void
  /**
   * Replaces the shared address search. Pass a stable function (memoized with
   * `useCallback`), because the suggestion effect re-runs when it changes.
   * When set, suggestions show without the address-autocomplete flag.
   */
  fetchSuggestions?: AddressSuggestionFetcher
  onBlur?: () => void
  disabled?: boolean
  inputClassName?: string
  variant?: 'outlined' | 'filled' | 'standard'
  size?: 'small' | 'medium' | 'large'
  fullWidth?: boolean
  autoComplete?: string
}

function mapSuggestionToAddress(suggestion: AddressSuggestionSchemaType): AddressAutocompleteValue {
  return {
    addressLine1: suggestion.address.line1,
    addressLine2: suggestion.address.line2 ?? '',
    city: suggestion.address.city,
    state: suggestion.address.state,
    zipCode: suggestion.address.postal_code,
  }
}

function suggestionOptionId(inputId: string, placeId: string): string {
  return `${inputId}-suggestion-${placeId}`
}

function trackSuggestionFailure(
  error: unknown,
  properties: {
    queryLengthBucket: ReturnType<typeof queryLengthBucket>
    portal: ReturnType<typeof getPortalType>
    surface: AddressAutocompleteSurface
  },
): void {
  // A 401 means the session is gone. Login redirect already handles that.
  if (getHttpResponseStatus(error) === 401) return
  trackEvent(ADDRESS_AUTOCOMPLETE_SUGGESTIONS_FAIL, {
    ...properties,
    ...errorAnalyticsPayload(error),
  })
}

/**
 * Address line 1 field. The parent owns `value`.
 *
 * The input tree stays mounted whether the address-autocomplete flag is
 * on, off, or still loading. Parents do not check the flag. Suggestions
 * and combobox semantics are applied only after the flag is on. Native
 * browser autofill stays enabled until the flag is on; then autocomplete
 * is off so it does not compete with suggestions.
 *
 * Typing or deleting calls `onChange` only. This component never calls
 * `onAddressSelect` and never invents city, state, or zip. A parent that
 * already filled those fields must leave them unchanged until the user
 * picks a suggestion.
 *
 * Picking a suggestion (mouse or keyboard) calls `onChange` with line 1,
 * then `onAddressSelect` with the mapped address so a parent can write
 * city, state, zip, and line 2. A later pick replaces those fields.
 *
 * If the user previously picked a suggestion, then edits line 1 and does
 * not pick again, only `onChange` runs. This component does not clear or
 * rewrite sibling fields.
 *
 * Suggestions are fetched only while the flag is on (or a parent passes
 * `fetchSuggestions`), the field is focused, and the query has at least
 * 3 characters. A pre-filled value does not fetch until focus.
 */
export function AddressAutocompleteInput({
  id,
  testId,
  label = '',
  value,
  onChange,
  onAddressSelect,
  onBlur,
  placeholder,
  required,
  error,
  className,
  labelClassName,
  showInfoIcon,
  disabled = false,
  inputClassName,
  variant,
  size,
  fullWidth,
  autoComplete,
  surface,
  fetchSuggestions,
}: Readonly<AddressAutocompleteInputProps>) {
  const isFlagEnabled = useAddressAutocompleteEnabled()
  const isAddressAutocompleteEnabled = fetchSuggestions ? true : isFlagEnabled
  const requestSuggestions = fetchSuggestions ?? getAddressSuggestions
  const listId = `${id}-suggestions`
  const containerRef = useRef<HTMLDivElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const lastSelectedQueryRef = useRef<string | null>(null)

  const [suggestions, setSuggestions] = useState<AddressSuggestionSchemaType[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)

  const debouncedValue = useDebounce(value, DEBOUNCE_MS)
  const showSuggestions =
    isAddressAutocompleteEnabled && !disabled && isOpen && suggestions.length > 0
  const highlightedSuggestion = suggestions[highlightedIndex]
  const clientPortal = isClientPortal()

  const selectSuggestion = (suggestion: AddressSuggestionSchemaType) => {
    abortControllerRef.current?.abort()
    lastSelectedQueryRef.current = suggestion.address.line1.trim()
    setIsOpen(false)
    setSuggestions([])
    setIsLoading(false)
    onChange(suggestion.address.line1)
    onAddressSelect(mapSuggestionToAddress(suggestion))
  }

  const handleInputChange = (nextValue: string) => {
    if (nextValue.trim() !== lastSelectedQueryRef.current) {
      lastSelectedQueryRef.current = null
    }
    onChange(nextValue)
  }

  useEffect(() => {
    if (!isAddressAutocompleteEnabled) return

    const handleMouseDown = (event: MouseEvent) => {
      const { target } = event
      if (!(target instanceof Node) || !containerRef.current?.contains(target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [isAddressAutocompleteEnabled])

  const trimmedQuery = debouncedValue.trim()
  const shouldClose =
    !isAddressAutocompleteEnabled ||
    disabled ||
    !isFocused ||
    trimmedQuery.length < MIN_QUERY_LENGTH
  const [prevShouldClose, setPrevShouldClose] = useState(shouldClose)
  if (prevShouldClose !== shouldClose) {
    setPrevShouldClose(shouldClose)
    if (shouldClose) {
      setIsOpen(false)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (shouldClose) {
      return
    }

    if (trimmedQuery === lastSelectedQueryRef.current) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- state synced from props in an effect; keep until refactor
      setIsLoading(false)
      return
    }

    const abortController = new AbortController()
    abortControllerRef.current = abortController
    let isCancelled = false

    const shouldIgnoreUpdates = () => isCancelled || abortController.signal.aborted

    const loadSuggestions = async () => {
      setIsLoading(true)
      try {
        const results = await requestSuggestions(trimmedQuery, {
          signal: abortController.signal,
        })

        if (shouldIgnoreUpdates()) return
        if (lastSelectedQueryRef.current === trimmedQuery) return

        setSuggestions(results)
        setHighlightedIndex(0)
        setIsOpen(results.length > 0)
      } catch (error) {
        if (shouldIgnoreUpdates() || isAbortError(error)) return
        setSuggestions([])
        setIsOpen(false)
        trackSuggestionFailure(error, {
          queryLengthBucket: queryLengthBucket(trimmedQuery.length),
          portal: getPortalType(),
          surface,
        })
      } finally {
        if (shouldIgnoreUpdates()) return
        setIsLoading(false)
      }
    }

    void loadSuggestions()

    return () => {
      isCancelled = true
      abortController.abort()
    }
  }, [
    debouncedValue,
    disabled,
    isAddressAutocompleteEnabled,
    isFocused,
    requestSuggestions,
    surface,
    shouldClose,
    trimmedQuery,
  ])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      if (isOpen) {
        event.preventDefault()
        setIsOpen(false)
      }
      return
    }

    if (event.key === 'Tab') {
      setIsOpen(false)
      return
    }

    if (!showSuggestions) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlightedIndex((index) => (index + 1) % suggestions.length)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlightedIndex((index) => (index - 1 + suggestions.length) % suggestions.length)
      return
    }

    if (event.key === 'Enter' && highlightedSuggestion) {
      event.preventDefault()
      selectSuggestion(highlightedSuggestion)
    }
  }

  return (
    <div ref={containerRef} className={cn('relative', fullWidth && 'w-full')}>
      <FormInput
        id={id}
        testId={testId}
        label={label}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false)
          setIsOpen(false)
          onBlur?.()
        }}
        onKeyDown={isAddressAutocompleteEnabled ? handleKeyDown : undefined}
        placeholder={placeholder}
        required={required}
        error={error}
        className={className}
        labelClassName={labelClassName}
        showInfoIcon={showInfoIcon}
        disabled={disabled}
        inputClassName={cn(
          'ph-no-capture notranslate',
          clientPortal && CLIENT_PORTAL_INPUT_FOCUS_CLASSES,
          inputClassName,
        )}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        autoComplete={isAddressAutocompleteEnabled ? 'off' : (autoComplete ?? 'address-line1')}
        aria-label={label || undefined}
        endIcon={
          isAddressAutocompleteEnabled && isLoading ? (
            <Loader2
              className="h-4 w-4 animate-spin"
              data-testid={`${testId}-loading`}
              aria-hidden
            />
          ) : undefined
        }
        role={isAddressAutocompleteEnabled ? 'combobox' : undefined}
        aria-autocomplete={isAddressAutocompleteEnabled ? 'list' : undefined}
        aria-expanded={isAddressAutocompleteEnabled ? showSuggestions : undefined}
        aria-controls={isAddressAutocompleteEnabled ? listId : undefined}
        aria-activedescendant={
          isAddressAutocompleteEnabled && showSuggestions && highlightedSuggestion
            ? suggestionOptionId(id, highlightedSuggestion.place_id)
            : undefined
        }
        aria-busy={isAddressAutocompleteEnabled ? isLoading : undefined}
      />

      {showSuggestions ? (
        <ul
          id={listId}
          role="listbox"
          data-testid={`${testId}-suggestions`}
          className={cn(
            'absolute z-50 w-full top-full',
            'max-h-[320px] overflow-auto p-1',
            'rounded-lg border shadow-lg',
            clientPortal
              ? 'bg-white border-light-grey'
              : 'bg-tenant-card-background text-tenant-card-foreground border-tenant-border',
          )}
        >
          {suggestions.map((suggestion, index) => {
            const isHighlighted = index === highlightedIndex
            return (
              <li
                key={suggestion.place_id}
                id={suggestionOptionId(id, suggestion.place_id)}
                role="option"
                aria-selected={isHighlighted}
                data-testid={`${testId}-suggestion-${suggestion.place_id}`}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-md cursor-pointer text-base',
                  isHighlighted && clientPortal && 'bg-light-purple text-bright-blue',
                  isHighlighted &&
                    !clientPortal &&
                    'bg-tenant-subtle-accent-background text-tenant-subtle-accent-foreground',
                )}
                tabIndex={-1}
                onMouseDown={(event) => {
                  event.preventDefault()
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => selectSuggestion(suggestion)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    selectSuggestion(suggestion)
                  }
                }}
              >
                <span className="ph-no-capture notranslate">{suggestion.label}</span>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
